import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { getJSON, getCSRFHeaders } from '@folklore/fetch';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React, { useMemo, useState, useRef, useCallback } from 'react';
import { getPathValue } from '@panneau/core/utils';
import Select from '@panneau/element-select';

var _excluded = ["value", "paginated", "loadOptions", "requestUrl", "requestQuery", "requestOptions", "requestSearchParamName", "prepareRequestOptions", "maxOptionsCount", "optionLabelPath", "optionValuePath", "getOptionLabel", "getOptionValue", "multiple", "className", "onChange", "onInputChange"];
var propTypes = {
  value: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types
  paginated: PropTypes.bool,
  loadOptions: PropTypes.func,
  requestUrl: PropTypes.string,
  requestOptions: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestQuery: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  requestSearchParamName: PropTypes.string,
  prepareRequestOptions: PropTypes.func,
  maxOptionsCount: PropTypes.number,
  optionLabelPath: PropTypes.string,
  optionValuePath: PropTypes.string,
  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func
};
var defaultProps = {
  value: null,
  paginated: false,
  loadOptions: null,
  requestUrl: null,
  requestOptions: null,
  requestQuery: null,
  requestSearchParamName: 'search',
  prepareRequestOptions: null,
  maxOptionsCount: null,
  optionLabelPath: null,
  optionValuePath: null,
  getOptionLabel: null,
  getOptionValue: null,
  multiple: false,
  className: null,
  onChange: null,
  onInputChange: null
};
var SelectField = function SelectField(_ref) {
  var value = _ref.value,
    paginated = _ref.paginated,
    customLoadOptions = _ref.loadOptions,
    requestUrl = _ref.requestUrl,
    requestQuery = _ref.requestQuery,
    requestOptions = _ref.requestOptions,
    requestSearchParamName = _ref.requestSearchParamName,
    prepareRequestOptions = _ref.prepareRequestOptions,
    maxOptionsCount = _ref.maxOptionsCount,
    optionLabelPath = _ref.optionLabelPath,
    optionValuePath = _ref.optionValuePath,
    customGetOptionLabel = _ref.getOptionLabel,
    customGetOptionValue = _ref.getOptionValue,
    multiple = _ref.multiple,
    className = _ref.className,
    onChange = _ref.onChange,
    customOnInputChange = _ref.onInputChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var getOptionLabel = useMemo(function () {
    return customGetOptionLabel || (optionLabelPath !== null ? function (option) {
      return getPathValue(option, optionLabelPath);
    } : null);
  }, [customGetOptionLabel, optionLabelPath]);
  var getOptionValue = useMemo(function () {
    return customGetOptionValue || (optionValuePath !== null ? function (option) {
      return getPathValue(option, optionValuePath);
    } : null);
  }, [customGetOptionValue, optionValuePath]);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    inputTextValue = _useState2[0],
    setInputTextValue = _useState2[1];
  var inputRef = useRef(false);
  var loadOptions = useMemo(function () {
    return requestUrl !== null ? function (requestValue) {
      var queryData = _objectSpread(_objectSpread(_objectSpread({
        paginated: paginated
      }, paginated ? {
        page: 1
      } : null), requestQuery), requestValue !== null && requestSearchParamName !== null ? _defineProperty({}, requestSearchParamName, requestValue) : null);
      var finalQuery = queryData !== null ? queryString.stringify(queryData, {
        arrayFormat: 'bracket'
      }) : null;
      return getJSON("".concat(requestUrl).concat(finalQuery !== null && finalQuery.length > 0 ? "?".concat(finalQuery) : ''), _objectSpread({
        credentials: 'include',
        headers: getCSRFHeaders()
      }, requestOptions)).then(function (newItems) {
        var _ref3 = paginated ? newItems || {} : {
            data: newItems || []
          },
          _ref3$data = _ref3.data,
          data = _ref3$data === void 0 ? null : _ref3$data;
        var finalNewItems = maxOptionsCount !== null ? data.slice(0, maxOptionsCount) : data;
        inputRef.current = false;
        return prepareRequestOptions !== null ? prepareRequestOptions(finalNewItems) : finalNewItems;
      })["catch"](function () {
        inputRef.current = false;
      });
    } : null;
  }, [maxOptionsCount, requestUrl, requestQuery, requestOptions, requestSearchParamName, prepareRequestOptions, paginated]);
  var finalLoadOptions = customLoadOptions || loadOptions || null;
  var onValueChange = useCallback(function (newValue) {
    if (onChange !== null) {
      onChange(newValue);
    }
    setInputTextValue('');
  }, [onChange, setInputTextValue]);
  var onInputChange = useCallback(function (textValue) {
    setInputTextValue(textValue);
    if (customOnInputChange !== null) {
      customOnInputChange(textValue);
    }
  }, [setInputTextValue, customOnInputChange]);
  return /*#__PURE__*/React.createElement(Select, Object.assign({
    className: className,
    value: value,
    isAsync: finalLoadOptions !== null,
    defaultOptions: finalLoadOptions !== null,
    loadOptions: finalLoadOptions,
    onChange: onValueChange,
    onInputChange: onInputChange,
    inputValue: inputTextValue,
    multiple: multiple
  }, getOptionLabel !== null ? {
    getOptionLabel: getOptionLabel
  } : null, getOptionValue !== null ? {
    getOptionValue: getOptionValue
  } : null, props));
};
SelectField.propTypes = propTypes;
SelectField.defaultProps = defaultProps;

var definition = {
  id: 'select',
  component: 'Select'
};

export { SelectField as default, definition };
